import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { getClipListDetails } from "store/clipList/actions";

import {
  getChapterizeLanguages,
  getPreStreamTemplates,
  getStreamByClipId,
  getStreamClipFilters,
  getStreamDetails,
  getStreamDetailsByStreamId,
  getStreams,
  getStreamsByIds,
  getStreamsListByUrl,
} from "./actions";

export const streamEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity.streamId,
});

const initialState = streamEntityAdapter.getInitialState({
  count: 0,
  modals: {},
  clipFilters: {},
  preStreamTemplates: [],
  preStreamTemplatesCount: 0,
  customIds: {},
  chapterizeLanguages: [],
});

export const streamSlice = createSlice({
  name: "stream",
  initialState: initialState,
  reducers: {
    showStreamAllFiltersModal: (state, action) => {
      state.modals.streamAllFiltersModal = action.payload;
    },
    showStreamDeleteModal: (state, action) => {
      state.modals.streamDeleteModal = action.payload;
    },
    showStreamReTriggerModal: (state, action) => {
      state.modals.streamReTriggerModal = action.payload;
    },
    showStreamConvertVideoModal: (state, action) => {
      state.modals.streamConvertVideoModal = action.payload;
    },
    showStreamScheduleLaterModal: (state, action) => {
      state.modals.streamScheduleLaterDatePickerModal = action.payload;
    },
    showScheduleStreamCycleModal: (state, action) => {
      state.modals.scheduleStreamCycleModal = action.payload;
    },
    showEndStreamModal: (state, action) => {
      state.modals.endStreamModal = action.payload;
    },
    showDownloadMatchVideoModal: (state, action) => {
      state.modals.downloadMatchVideoModal = action.payload;
    },
    showExportToCloudModal: (state, action) => {
      state.modals.exportToCloudModal = action.payload;
    },
    clearStreamTemplates: (state) => {
      state.preStreamTemplates = [];
      state.preStreamTemplatesCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStreams.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.meta.arg.requestType === "append") {
          if (action.meta.arg.customId === "liveStreamIds") {
            streamEntityAdapter.removeAll(state);
          }

          streamEntityAdapter.addMany(state, action.payload.stream);
          if (!!action.meta.arg.customId) {
            state.customIds[action.meta.arg.customId] = action.payload.stream.map((stream) => stream.streamId);
          }
        } else {
          streamEntityAdapter.setAll(state, action.payload.stream);
        }
        state.count = action.payload.total_streams;
      } else {
        streamEntityAdapter.removeAll(state);
        state.count = 0;
      }
    });
    builder.addCase(getStreamsByIds.fulfilled, (state, action) => {
      streamEntityAdapter.setAll(state, action.payload?.data || []);
    });
    builder.addCase(getStreamDetails.fulfilled, (state, action) => {
      if (action.payload.success) {
        state.entities[action.payload.stream.streamId] = {
          ...state.entities[action.payload.stream.streamId],
          ...action.payload.stream,
          clipsCount: action.payload.stream.clipsCount || state.entities[action.payload.stream.streamId]?.clipsCount,
        };
      }
    });
    builder.addCase(getStreamClipFilters.fulfilled, (state, action) => {
      const { streamId, filters } = action.payload;
      if (streamId && filters) {
        // api responds with a single array element
        state.clipFilters[streamId] = filters?.[0];
      }
    });
    builder.addCase(getStreamsListByUrl.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.meta.arg.upsert) {
          streamEntityAdapter.upsertMany(state, action.payload.streamsList);
        } else {
          streamEntityAdapter.setAll(state, action.payload.streamsList);
        }
      } else {
        if (!action.meta.arg.upsert) {
          streamEntityAdapter.removeAll(state);
          state.count = 0;
        }
      }
    });
    builder.addCase(getPreStreamTemplates.fulfilled, (state, action) => {
      if (action.payload.success) {
        state.preStreamTemplates = action.payload.data?.templates;
        state.preStreamTemplatesCount = action.payload.data?.totalCount;
      } else {
        state.preStreamTemplates = [];
        state.preStreamTemplatesCount = 0;
      }
    });
    builder.addCase(getStreamDetailsByStreamId.fulfilled, (state, action) => {
      if (action.payload.success && action.payload.stream?.streamId) {
        state.entities[action.payload.stream.streamId] = {
          ...state.entities[action.payload.stream.streamId],
          ...action.payload.stream,
          clipsCount: action.payload.stream.clipsCount || state.entities[action.payload.stream.streamId]?.clipsCount,
        };
      }
    });
    builder.addCase(getStreamByClipId.fulfilled, (state, action) => {
      if (action.payload.success) {
        const stream = action.payload.data;

        streamEntityAdapter.addOne(state, stream);
      }
    });
    builder.addCase(getClipListDetails.fulfilled, (state, action) => {
      if (action.payload.success) {
        const { entities } = action.payload;
        const stream = entities?.stream || {};
        const clips = entities?.clip || {};

        const idToStreamIdMap = Object.values(clips).reduce((acc, clip) => {
          if (clip?.streamId) {
            acc[clip.stream] = clip.streamId;

            return acc;
          }

          return acc;
        }, {});

        if (stream && Object.keys(stream).length > 0) {
          Object.keys(stream).forEach((id) => {
            const streamId = idToStreamIdMap[id];

            if (streamId) {
              state.entities[streamId] = {
                ...(state.entities?.[streamId] || {}),
                ...stream[id],
              };
            }
          });
        }
      }
    });
    builder.addCase(getChapterizeLanguages.fulfilled, (state, action) => {
      state.chapterizeLanguages = action.payload?.value || [];
    });
  },
});

export const {
  showStreamAllFiltersModal,
  showStreamDeleteModal,
  showStreamReTriggerModal,
  showStreamConvertVideoModal,
  showStreamScheduleLaterModal,
  showScheduleStreamCycleModal,
  showEndStreamModal,
  showDownloadMatchVideoModal,
  showExportToCloudModal,
  clearStreamTemplates,
} = streamSlice.actions;

export default streamSlice.reducer;
